import type { CoreConfiguration, DropinConfiguration } from '@adyen/adyen-web';
import React, { useRef, useState } from 'react';

import Message from '@sats-group/ui-lib/react/message/message';
import type { Message as MessageProps } from '@sats-group/ui-lib/react/message/message.types';
import Text from '@sats-group/ui-lib/react/text';

import { trackCustomException } from 'shared/application-insights/application-insights-web';
import ContentContainer from 'shared/components/content-container/content-container';
import Logo from 'shared/components/logo/logo';
import PaymentSummary from 'shared/components/payment-summary/payment-summary';
import { type Core, useAdyen } from 'shared/hooks/use-adyen';
import { useMessage } from 'shared/hooks/use-message';
import type { NamedFC } from 'shared/types/named-fc.types';

import useAsyncEffect from 'ui/pages/payment/hooks/use-async-effect';
import getAdyenLocale from 'ui/pages/payment/utils/get-adyen-locale';

import type { PayOnOwnDevicePayment as Props } from './pay-on-own-device-payment.props';

const PayOnOwnDevicePayment: NamedFC<Props> = ({
  adyenClientKey,
  adyenDisclaimer,
  adyenEnvironment,
  adyenTitle,
  consent,
  language,
  logo,
  onSuccessUrl,
  paymentErrorMessage,
  paymentModuleLoadingErrorMessage,
  paymentRefusedMessage,
  paymentSummary,
  session,
}) => {
  const [paymentMessages, setPaymentMessages] = useState<MessageProps[]>([]);
  const checkoutRef = useRef<Core>(undefined);
  const { messagesQueue, getAction } = useMessage(paymentMessages, language);

  const [, hasAdyenLoaded] = useAdyen(() =>
    setPaymentMessages([
      ...paymentMessages,
      {
        text: paymentModuleLoadingErrorMessage,
        theme: 'error',
      },
    ])
  );

  useAsyncEffect(async () => {
    if (!hasAdyenLoaded || !window.AdyenWeb || !onSuccessUrl) {
      return;
    }

    if (!session) {
      return;
    }

    const adyenConfiguration: CoreConfiguration = {
      environment: adyenEnvironment, // Change to 'live' for the live environment.
      clientKey: adyenClientKey, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
      locale: getAdyenLocale(language),
      session: session,
      onPaymentCompleted: response => {
        switch (response.resultCode) {
          case 'Authorised': {
            window.location.assign(onSuccessUrl);
            return;
          }
          case 'Refused': {
            trackCustomException('Adyen payment refused', {
              resultCode: response.resultCode,
            });

            setPaymentMessages([
              {
                text: paymentRefusedMessage,
                theme: 'error',
              },
            ]);

            if (checkoutRef.current) {
              checkoutRef.current.update(checkoutRef.current.options); // NOTE: No sure if this should be what is passed in here. But the update method expects an argument.
            }

            break;
          }
          case 'Error':
          default: {
            trackCustomException('Adyen payment error', {
              resultCode: response.resultCode,
            });

            setPaymentMessages([
              {
                text: paymentErrorMessage,
                theme: 'error',
              },
            ]);

            if (checkoutRef.current) {
              checkoutRef.current.update(checkoutRef.current.options); // NOTE: No sure if this should be what is passed in here. But the update method expects an argument.
            }
          }
        }
      },
      onError: error => {
        trackCustomException(error, {
          source: 'adyen-on-error',
        });

        setPaymentMessages([
          {
            text: paymentErrorMessage,
            theme: 'error',
          },
        ]);

        if (checkoutRef.current) {
          checkoutRef.current.update(checkoutRef.current.options); // NOTE: No sure if this should be what is passed in here. But the update method expects an argument.
        }
      },
    };

    const dropinConfiguration: DropinConfiguration = {
      openFirstPaymentMethod: true,
      openPaymentMethod: {
        type: 'applepay',
      },
      // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
      // For example, this is 3D Secure configuration for cards:
      paymentMethodsConfiguration: {
        card: {
          challengeWindowSize: '05',
          name: adyenTitle,
          hasHolderName: true,
          holderNameRequired: true,
        },
      },
      showRadioButton: true,
    };

    const { AdyenCheckout, Dropin } = window.AdyenWeb;
    const checkout = await AdyenCheckout(adyenConfiguration);
    checkoutRef.current = checkout;
    new Dropin(checkout, dropinConfiguration).mount('#dropin-container');
    // checkout.create('dropin').mount(`#dropin-container`);
    setPaymentMessages([]);
  }, [hasAdyenLoaded]);

  return (
    <div className="pay-on-own-device-payment">
      <ContentContainer theme="narrow">
        {messagesQueue.length ? (
          <div className="pay-on-own-device-payment__messages">
            {messagesQueue.map(message => (
              <Message
                key={message.text}
                {...message}
                action={getAction(message)}
              />
            ))}
          </div>
        ) : null}
        <div className="pay-on-own-device-payment__logo">
          <Logo {...logo} />
        </div>
        <div className="pay-on-own-device-payment__payment-information">
          {paymentSummary ? <PaymentSummary {...paymentSummary} /> : null}
          <div id="dropin-container"></div>
        </div>
        <div className="pay-on-own-device-payment__disclaimer">
          <Text size={Text.sizes.interface}>{consent}</Text>
          <Text
            className="pay-on-own-device-payment__adyen-disclaimer"
            size={Text.sizes.interface}
          >
            {adyenDisclaimer}
          </Text>
        </div>
      </ContentContainer>
    </div>
  );
};

PayOnOwnDevicePayment.displayName = 'PayOnOwnDevicePayment';

export default PayOnOwnDevicePayment;
